import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TextareaAutosize from "react-autosize-textarea";

const ConsultationNotesDialog = ({
  open,
  onClose,
  onSave,
  consultationNotes = "",
  isSaving = false,
  error = "",
  t,
}) => {
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setNotes(consultationNotes || "");
    }
  }, [open, consultationNotes]);

  const handleClose = () => {
    if (!isSaving) {
      setNotes("");
      onClose();
    }
  };

  const handleSave = () => {
    if (!isSaving && onSave) {
      onSave(notes);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth={"sm"}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 18px 38px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
          fontWeight: 700,
          fontSize: "1.15rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {t("Console.ActionPopup.MarkConsultationManual", {
          defaultValue: "Mark Consultation Done",
        })}
        <IconButton
          aria-label={t("Global.Close", { defaultValue: "Close" })}
          onClick={handleClose}
          disabled={isSaving}
          sx={{
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.18)",
            },
            "&:disabled": {
              color: "rgba(255, 255, 255, 0.5)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          backgroundColor: "#ffffff",
        }}
      >
        <Box>
          {/* Show existing notes if editing */}
          {consultationNotes && consultationNotes.trim() !== "" && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: "#f0fdf4",
                borderRadius: 2,
                border: "1px solid #22c55e",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "#166534",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: 1,
                  display: "block",
                }}
              >
                {t("Console.ActionPopup.CurrentNotes", {
                  defaultValue: "Current Notes:",
                })}
              </Typography>
              <Typography
                sx={{
                  color: "#1e293b",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  fontStyle: "italic",
                }}
              >
                {consultationNotes}
              </Typography>
            </Box>
          )}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              mb: 1.5,
            }}
          >
            {t("Report.Consult.ConsultationNotes", {
              defaultValue: "Consultation Notes",
            })}
          </Typography>
          <TextareaAutosize
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSaving}
            placeholder={t("Report.Consult.ConsultationNotes", {
              defaultValue: "Enter consultation notes...",
            })}
            style={{
              minHeight: 150,
              maxHeight: 300,
              width: "100%",
              boxSizing: "border-box",
              border: error
                ? "2px solid #ef4444"
                : "1px solid rgba(224, 224, 224, 1)",
              borderRadius: "8px",
              fontSize: "0.875rem",
              fontWeight: "400",
              padding: "12px",
              fontFamily: "Roboto,Helvetica,Arial,sans-serif",
              color: "rgba(0, 0, 0, 0.87)",
              backgroundColor: isSaving ? "#f1f5f9" : "#ffffff",
              resize: "vertical",
            }}
          />
          {error && (
            <Typography
              sx={{
                color: "#ef4444",
                fontSize: "0.75rem",
                mt: 0.5,
                ml: 1,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isSaving}
          sx={{
            minWidth: "120px",
            minHeight: "43px",
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            borderWidth: 2,
            borderColor: "#cbd5e1",
            color: "#1e293b",
            "&:hover": {
              borderColor: "#94a3b8",
              backgroundColor: "rgba(148, 163, 184, 0.08)",
            },
            "&:disabled": {
              borderColor: "#cbd5e1",
              color: "#94a3b8",
            },
          }}
        >
          {t("Global.Cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
          sx={{
            minWidth: "120px",
            minHeight: "43px",
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            boxShadow: "0 8px 18px rgba(124, 58, 237, 0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
              boxShadow: "0 10px 24px rgba(124, 58, 237, 0.4)",
            },
            "&:disabled": {
              background: "#b0bec5",
              color: "#ffffff",
              boxShadow: "none",
            },
          }}
        >
          {isSaving ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={16} sx={{ color: "#ffffff" }} />
              <span>
                {t("Console.ActionPopup.Saving", {
                  defaultValue: "Saving...",
                })}
              </span>
            </Box>
          ) : (
            t("Global.Save", { defaultValue: "Save" })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConsultationNotesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  consultationNotes: PropTypes.string,
  isSaving: PropTypes.bool,
  error: PropTypes.string,
  t: PropTypes.func.isRequired,
};

ConsultationNotesDialog.defaultProps = {
  consultationNotes: "",
  isSaving: false,
  error: "",
};

export default ConsultationNotesDialog;




// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/ConsultationNotesDialog.jsx